// src/pages/auth/ResetPasswordPage.tsx
//
// The page the password-reset email opens: the API mails
// FRONTEND_BASE_URL + "/reset-password?token=...". Until now that route did not
// exist, so every reset link landed on the 404 page.
//
// The email carries only the token, while the API's reset endpoint also
// requires the address (binding:"required,email"), so the form asks for it. It
// doubles as a check that the link was opened by the person who asked for it.
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { getUserApi } from '@/lib/api/client';
import { toCompatError } from '@/lib/api/apiUtils';
import { friendlyAuthErrorText } from '@/lib/api/authErrors';
import { InfoBox } from '@/components/custom-ui/info-box';
import { AuthShell } from './AuthShell';

const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') ?? '';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password.length < 8) {
            setError('Lösenordet måste vara minst 8 tecken.');
            return;
        }
        if (password !== confirm) {
            setError('Lösenorden matchar inte.');
            return;
        }

        setIsSubmitting(true);
        try {
            await getUserApi().resetPassword({
                email: email.trim(),
                new_password: password,
                reset_token: token,
            });
            setDone(true);
        } catch (err) {
            setError(friendlyAuthErrorText(toCompatError(err)));
        } finally {
            setIsSubmitting(false);
        }
    };

    // No token in the URL — the person got here by hand, or the link was
    // truncated by their mail client. Say so instead of showing a form that
    // cannot succeed.
    if (!token) {
        return (
            <AuthShell title="Länken är ofullständig" subtitle="Vi hittar ingen återställningskod i länken.">
                <div className="space-y-4">
                    <InfoBox variant="error">
                        Öppna länken från mejlet igen, eller begär en ny återställningslänk.
                    </InfoBox>
                    <Link
                        to="/forgot-password"
                        className="w-full flex items-center justify-center px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all text-sm"
                    >
                        Begär en ny länk
                    </Link>
                </div>
            </AuthShell>
        );
    }

    if (done) {
        return (
            <AuthShell title="Lösenordet är ändrat" subtitle="Du kan logga in med ditt nya lösenord.">
                <div className="flex flex-col items-center text-center gap-4">
                    <div className="w-14 h-14 bg-[#A4C3B2] rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-7 h-7 text-[#1A4D4F]" />
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate('/')}
                        className="w-full flex items-center justify-center px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all text-sm"
                    >
                        Logga in
                    </button>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell title="Välj ett nytt lösenord" subtitle="Länken gäller i en timme från att den skickades.">
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && <InfoBox variant="error">{error}</InfoBox>}

                <div>
                    <label className="block text-xs font-semibold mb-1.5">E-post *</label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={isSubmitting}
                            autoFocus
                            className="w-full pl-10 pr-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:bg-gray-50 transition-all"
                            placeholder="din.email@exempel.se"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-semibold mb-1.5">Nytt lösenord *</label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                            disabled={isSubmitting}
                            className="w-full pl-10 pr-10 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:bg-gray-50 transition-all"
                            placeholder="Minst 8 tecken"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-semibold mb-1.5">Bekräfta lösenord *</label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                            disabled={isSubmitting}
                            className="w-full pl-10 pr-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:bg-gray-50 transition-all"
                            placeholder="Upprepa lösenordet"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={isSubmitting || !email || !password || !confirm}
                    className="w-full flex items-center justify-center px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                    {isSubmitting ? 'Sparar...' : 'Spara nytt lösenord'}
                </button>
            </form>
        </AuthShell>
    );
};

export default ResetPasswordPage;
